import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PlusIcon, Loader2Icon, AlertTriangleIcon } from 'lucide-react';
import { AssignmentsModal } from './AssignmentsModal';
import type { Assignment as ModalAssignment } from './AssignmentsModal';
import { SlidePanel, AssignmentSettingsModal } from './AssignmentSettingsModal';
import type { LearnerTarget } from './AssignmentSettingsModal';
export interface Assignment {
  id: number;
  label: string;
  distributionType: 'link' | 'group' | 'classroom';
  status: 'Active' | 'Closed';
  responseCount: number;
  link: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  timeLimit?: number;
  groupName?: string;
}
interface AssignmentsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  assessment: {
    id: number;
    title: string;
    assignments: Assignment[];
  } | null;
  onUpdateAssignments: (assessmentId: number, assignments: Assignment[]) => void;
  autoCreateNew?: boolean;
}
// Convert internal Assignment → AssignmentsModal Assignment format
const toModalAssignment = (a: Assignment): ModalAssignment => ({
  id: String(a.id),
  label: a.label,
  link: a.link,
  createdAt: new Date().toISOString(),
  createdDate: new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }),
  responses: a.responseCount,
  status: a.status === 'Active' ? 'active' : 'closed',
  settings: {
    distributionType: a.distributionType,
    groupName: a.groupName,
    scheduledStart: a.scheduledStart,
    scheduledEnd: a.scheduledEnd,
    timeLimitMinutes: a.timeLimit
  }
});
export function AssignmentsPanel({
  isOpen,
  onClose,
  assessment,
  onUpdateAssignments,
  autoCreateNew = false
}: AssignmentsPanelProps) {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [processedAutoCreate, setProcessedAutoCreate] = useState(false);
  // Settings panel state
  const [settingsAssignmentId, setSettingsAssignmentId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'classrooms' | 'groups'>('classrooms');
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [scheduleMode, setScheduleMode] = useState<'always' | 'specific'>('always');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const [timeLimitMode, setTimeLimitMode] = useState<'none' | 'timed'>('none');
  const [timeLimitValue, setTimeLimitValue] = useState('30');
  // Placeholder learner targets (would come from real data in production)
  const learnerTargets: LearnerTarget[] = [];
  useEffect(() => {
    if (assessment) {
      setAssignments(assessment.assignments || []);
    }
  }, [assessment]);
  useEffect(() => {
    if (isOpen && autoCreateNew && !processedAutoCreate && assessment) {
      const activeAssignments = (assessment.assignments || []).filter(a => a.status === 'Active');
      if (activeAssignments.length > 0) {
        setShowConflictModal(true);
      } else {
        handleCreateAssignment(false);
      }
      setProcessedAutoCreate(true);
    }
  }, [isOpen, autoCreateNew, processedAutoCreate, assessment]);
  useEffect(() => {
    if (!isOpen) {
      setProcessedAutoCreate(false);
      setShowConflictModal(false);
    }
  }, [isOpen]);
  const handleCreateAssignment = (shouldCloseOthers: boolean = false) => {
    setIsCreating(true);
    setShowConflictModal(false);
    setTimeout(() => {
      let currentAssignments = [...assignments];
      if (shouldCloseOthers) {
        currentAssignments = currentAssignments.map(a => ({
          ...a,
          status: 'Closed' as const
        }));
      }
      let nextNumber = 1;
      if (!shouldCloseOthers) {
        const regex = /Assignment #(\d+)/;
        let maxNum = 0;
        currentAssignments.forEach(a => {
          const match = a.label.match(regex);
          if (match) {
            const num = parseInt(match[1]);
            if (num > maxNum) maxNum = num;
          }
        });
        nextNumber = maxNum + 1;
      }
      const dateStr = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      const newAssignment: Assignment = {
        id: Date.now(),
        label: `${assessment?.title || 'Assessment'} - Assignment #${nextNumber} - ${dateStr}`,
        distributionType: 'link',
        status: 'Active',
        responseCount: 0,
        link: `https://app.ainstein.com/a/${Math.random().toString(36).substring(7)}`
      };
      const updatedAssignments = [newAssignment, ...currentAssignments];
      setAssignments(updatedAssignments);
      if (assessment) onUpdateAssignments(assessment.id, updatedAssignments);
      setIsCreating(false);
    }, 600);
  };
  const handleEditAssignment = async (id: string, newLabel: string): Promise<boolean> => {
    const isDuplicate = assignments.some(a => String(a.id) !== id && a.label === newLabel);
    if (isDuplicate) return false;
    const updatedAssignments = assignments.map(a => String(a.id) === id ? {
      ...a,
      label: newLabel
    } : a);
    setAssignments(updatedAssignments);
    if (assessment) onUpdateAssignments(assessment.id, updatedAssignments);
    return true;
  };
  const handleCloseAssignment = (id: string) => {
    const updatedAssignments = assignments.map(a => String(a.id) === id ? {
      ...a,
      status: 'Closed' as const
    } : a);
    setAssignments(updatedAssignments);
    if (assessment) onUpdateAssignments(assessment.id, updatedAssignments);
  };
  // Open settings panel and pre-populate state from assignment
  const handleSettingsClick = (id: string) => {
    const assignment = assignments.find(a => String(a.id) === id);
    if (!assignment) return;
    // Pre-populate from existing settings
    if (assignment.distributionType === 'group') {
      setActiveTab('groups');
      const target = learnerTargets.find(t => t.type === 'group' && t.name === assignment.groupName);
      setSelectedTargetId(target?.id || null);
    } else if (assignment.distributionType === 'classroom') {
      setActiveTab('classrooms');
      const target = learnerTargets.find(t => t.type === 'classroom' && t.name === assignment.groupName);
      setSelectedTargetId(target?.id || null);
    } else {
      setSelectedTargetId(null);
    }
    if (assignment.scheduledStart || assignment.scheduledEnd) {
      setScheduleMode('specific');
      if (assignment.scheduledStart) {
        const s = new Date(assignment.scheduledStart);
        s.setMinutes(s.getMinutes() - s.getTimezoneOffset());
        setStartDate(s.toISOString().slice(0, 16));
      }
      if (assignment.scheduledEnd) {
        const e = new Date(assignment.scheduledEnd);
        e.setMinutes(e.getMinutes() - e.getTimezoneOffset());
        setEndDate(e.toISOString().slice(0, 16));
      }
    } else {
      setScheduleMode('always');
      setStartDate('');
      setEndDate('');
    }
    if (assignment.timeLimit) {
      setTimeLimitMode('timed');
      setTimeLimitValue(String(assignment.timeLimit));
    } else {
      setTimeLimitMode('none');
      setTimeLimitValue('30');
    }
    setSelectedDuration(null);
    setSettingsAssignmentId(id);
  };
  const handleSaveSettings = () => {
    if (!settingsAssignmentId) return;
    let distributionType: 'link' | 'group' | 'classroom' = 'link';
    let groupName: string | undefined;
    if (selectedTargetId) {
      const target = learnerTargets.find(t => t.id === selectedTargetId);
      if (target) {
        distributionType = target.type;
        groupName = target.name;
      }
    }
    const updatedAssignments = assignments.map(a => String(a.id) === settingsAssignmentId ? {
      ...a,
      distributionType,
      groupName,
      scheduledStart: scheduleMode === 'specific' && startDate ? new Date(startDate).toISOString() : undefined,
      scheduledEnd: scheduleMode === 'specific' && endDate ? new Date(endDate).toISOString() : undefined,
      timeLimit: timeLimitMode === 'timed' ? parseInt(timeLimitValue) || undefined : undefined
    } : a);
    setAssignments(updatedAssignments);
    if (assessment) onUpdateAssignments(assessment.id, updatedAssignments);
    setSettingsAssignmentId(null);
  };
  const handleDurationSelect = (days: number) => {
    const base = startDate ? new Date(startDate) : new Date();
    const end = new Date(base);
    end.setDate(end.getDate() + days);
    end.setHours(23, 59, 0, 0);
    const endLocal = new Date(end.getTime() - end.getTimezoneOffset() * 60000);
    setEndDate(endLocal.toISOString().slice(0, 16));
    setSelectedDuration(days);
    if (!startDate) {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setStartDate(now.toISOString().slice(0, 16));
    }
  };
  const activeAssignmentsCount = assignments.filter(a => a.status === 'Active').length;
  const modalAssignments = assignments.map(toModalAssignment);
  const settingsAssignment = assignments.find(a => String(a.id) === settingsAssignmentId);
  if (!isOpen) return null;
  return <>
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60]" data-id="element-656" />
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-surface-elevated shadow-2xl z-[70] border-l border-custom flex flex-col" data-id="element-657">
        {/* Header */}
        <div className="px-6 py-5 border-b border-custom flex items-start justify-between gap-4 bg-surface" data-id="element-658">
          <div data-id="element-659">
            <h2 className="text-lg font-bold text-text-primary line-clamp-1" data-id="element-660">
              {assessment?.title}
            </h2>
            <p className="text-sm text-text-secondary" data-id="element-661">Manage assignments</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-662">
            <button onClick={() => {
            if (activeAssignmentsCount > 0) {
              setShowConflictModal(true);
            } else {
              handleCreateAssignment(false);
            }
          }} disabled={isLoading || isCreating} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00B75F] text-white rounded-lg text-sm font-semibold hover:bg-[#00a055] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" data-id="element-663">
              {isCreating ? <Loader2Icon size={13} className="animate-spin" data-id="element-664" /> : <PlusIcon size={13} data-id="element-665" />}
              New
            </button>
            <button onClick={onClose} className="p-2 -mr-2 text-text-tertiary hover:text-text-primary hover:bg-surface-elevated rounded-lg transition-colors" data-id="element-666">
              <XIcon size={20} data-id="element-667" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-668">
          <AssignmentsModal assignments={modalAssignments} isLoading={isLoading} isCreating={isCreating} onEditAssignment={handleEditAssignment} onCloseAssignment={handleCloseAssignment} onSettingsClick={handleSettingsClick} data-id="element-669" />
        </div>
      </motion.div>

      {/* Settings Panel — own framer-motion slide-in at z-[100] to sit above assignments panel */}
      <AnimatePresence data-id="element-670">
        {settingsAssignmentId && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSettingsAssignmentId(null)} className="fixed inset-0 bg-black/30 z-[90]" data-id="element-671" />
            {/* Panel */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 h-full w-full sm:w-[448px] bg-surface-elevated shadow-2xl z-[100] border-l border-custom flex flex-col" data-id="element-672">
              {/* Settings Header */}
              <div className="px-6 py-5 border-b border-custom flex items-start justify-between gap-4 bg-surface flex-shrink-0" data-id="element-673">
                <div data-id="element-674">
                  <h2 className="text-lg font-bold text-text-primary" data-id="element-675">
                    Assignment Settings
                  </h2>
                  {settingsAssignment && <p className="text-sm text-text-secondary line-clamp-1" data-id="element-676">
                      Configure "{settingsAssignment.label}"
                    </p>}
                </div>
                <button onClick={() => setSettingsAssignmentId(null)} className="p-2 -mr-2 text-text-tertiary hover:text-text-primary hover:bg-surface-elevated rounded-lg transition-colors" data-id="element-677">
                  <XIcon size={20} data-id="element-678" />
                </button>
              </div>

              {/* Settings Content */}
              <div className="flex-1 overflow-y-auto" data-id="element-679">
                <AssignmentSettingsModal activeTab={activeTab} targets={learnerTargets} selectedTargetId={selectedTargetId} onTabChange={setActiveTab} onTargetSelect={setSelectedTargetId} scheduleMode={scheduleMode} onScheduleModeChange={setScheduleMode} startDate={startDate} onStartDateChange={date => {
              setStartDate(date);
              setSelectedDuration(null);
            }} endDate={endDate} onEndDateChange={date => {
              setEndDate(date);
              setSelectedDuration(null);
            }} selectedDuration={selectedDuration} onDurationSelect={handleDurationSelect} timeLimitMode={timeLimitMode} onTimeLimitModeChange={setTimeLimitMode} timeLimitValue={timeLimitValue} onTimeLimitValueChange={setTimeLimitValue} onResetSchedule={() => {
              const now = new Date();
              now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
              setStartDate(now.toISOString().slice(0, 16));
              setEndDate('');
              setSelectedDuration(null);
            }} data-id="element-680" />
              </div>

              {/* Settings Footer */}
              <div className="bg-surface border-t border-custom px-6 py-4 flex justify-end gap-3 flex-shrink-0" data-id="element-681">
                <button onClick={() => setSettingsAssignmentId(null)} className="px-4 py-2 text-sm font-semibold text-text-secondary hover:text-text-primary hover:bg-surface-elevated rounded-lg transition-colors" data-id="element-682">
                  Cancel
                </button>
                <button onClick={handleSaveSettings} className="px-4 py-2 text-sm font-semibold text-white bg-[#00B75F] hover:bg-[#00a055] rounded-lg transition-colors" data-id="element-683">
                  Save Settings
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Conflict Modal */}
      <AnimatePresence data-id="element-684">
        {showConflictModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[80]" onClick={() => setShowConflictModal(false)} data-id="element-685" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-surface-elevated rounded-2xl shadow-2xl z-[90] border border-custom p-6" data-id="element-686">
              <div className="flex items-start gap-4 mb-4" data-id="element-687">
                <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0" data-id="element-688">
                  <AlertTriangleIcon size={20} data-id="element-689" />
                </div>
                <div data-id="element-690">
                  <h3 className="text-lg font-bold text-text-primary" data-id="element-691">
                    Active Assignments Exist
                  </h3>
                  <p className="text-sm text-text-secondary mt-1" data-id="element-692">
                    You already have active assignments for this assessment.
                    Each assignment has its own report.
                  </p>
                </div>
              </div>
              <div className="space-y-3" data-id="element-693">
                <button onClick={() => handleCreateAssignment(true)} className="w-full flex items-center justify-between p-3 rounded-xl border border-custom hover:border-[#00B75F] hover:bg-surface transition-all group text-left" data-id="element-694">
                  <div data-id="element-695">
                    <span className="block text-sm font-bold text-text-primary group-hover:text-[#00B75F]" data-id="element-696">
                      Close Previous & Create New
                    </span>
                    <span className="block text-xs text-text-secondary mt-0.5" data-id="element-697">
                      Recommended. Closes old assignments but keeps reports.
                    </span>
                  </div>
                  <div className="w-5 h-5 rounded-full border border-custom group-hover:border-[#00B75F] flex items-center justify-center" data-id="element-698">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00B75F] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-699" />
                  </div>
                </button>
                <button onClick={() => handleCreateAssignment(false)} className="w-full flex items-center justify-between p-3 rounded-xl border border-custom hover:border-[#00B75F] hover:bg-surface transition-all group text-left" data-id="element-700">
                  <div data-id="element-701">
                    <span className="block text-sm font-bold text-text-primary group-hover:text-[#00B75F]" data-id="element-702">
                      Keep Active & Add Another
                    </span>
                    <span className="block text-xs text-text-secondary mt-0.5" data-id="element-703">
                      Create a new assignment alongside existing ones.
                    </span>
                  </div>
                  <div className="w-5 h-5 rounded-full border border-custom group-hover:border-[#00B75F] flex items-center justify-center" data-id="element-704">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00B75F] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-705" />
                  </div>
                </button>
              </div>
              <div className="mt-6 flex justify-end" data-id="element-706">
                <button onClick={() => setShowConflictModal(false)} className="px-4 py-2 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors" data-id="element-707">
                  Cancel
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}